/**
 * Global Job Tracker Context
 *
 * Provides global state management for job tracking across all pages.
 * Jobs persist in session storage so users can navigate freely during generation.
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { useWebSocket } from '@/hooks/useWebSocket';
import { apiClient } from '@/utils/apiClient';

export type JobType = 'meditation' | 'zen' | 'affirmation' | 'sleep' | 'unknown';

export interface TrackedJob {
  id: string;
  type: JobType;
  status: 'queued' | 'processing' | 'completed' | 'failed' | 'cancelled';
  progress: number;
  queuePosition?: number;
  estimatedCompletion?: string;
  errorMessage?: string;
  resultData?: {
    sessionId?: string;
    audioUrl?: string;
    duration?: number;
  };
  createdAt: string;
  completedAt?: string;
  // Track whether user has acknowledged completion/failure
  acknowledged: boolean;
}

interface JobTrackerContextType {
  // State
  activeJobs: TrackedJob[];
  recentlyCompleted: TrackedJob[];
  recentlyFailed: TrackedJob[];

  // Actions
  trackJob: (jobId: string, type: JobType) => void;
  acknowledgeJob: (jobId: string) => void;
  clearAcknowledgedJobs: () => void;

  // Computed
  hasActiveJobs: boolean;
  hasUnacknowledgedCompletions: boolean;
  hasUnacknowledgedFailures: boolean;
}

const JobTrackerContext = createContext<JobTrackerContextType | undefined>(undefined);

const STORAGE_KEY = 'voicethera_tracked_jobs';
const MAX_RECENT_JOBS = 10;
const JOB_RETENTION_MS = 24 * 60 * 60 * 1000; // Keep jobs for 24 hours

export const JobTrackerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [jobs, setJobs] = useState<Map<string, TrackedJob>>(new Map());
  const pollingRef = useRef<NodeJS.Timeout | null>(null);

  // Load jobs from session storage on mount
  useEffect(() => {
    if (!user) return;

    try {
      const stored = sessionStorage.getItem(`${STORAGE_KEY}_${user.id}`);
      if (stored) {
        const parsed = JSON.parse(stored) as TrackedJob[];
        const now = Date.now();
        // Filter out old jobs
        const validJobs = parsed.filter(job => {
          const createdAt = new Date(job.createdAt).getTime();
          return now - createdAt < JOB_RETENTION_MS;
        });
        setJobs(new Map(validJobs.map(job => [job.id, job])));
      }
    } catch (e) {
      console.error('Failed to load tracked jobs:', e);
    }
  }, [user]);

  // Save jobs to session storage when they change
  useEffect(() => {
    if (!user) return;

    try {
      const jobsArray = Array.from(jobs.values());
      sessionStorage.setItem(`${STORAGE_KEY}_${user.id}`, JSON.stringify(jobsArray));
    } catch (e) {
      console.error('Failed to save tracked jobs:', e);
    }
  }, [jobs, user]);

  // Handle WebSocket updates
  const handleWebSocketMessage = useCallback((message: any) => {
    if (message.type !== 'job_update') return;

    const jobId = message.job_id;
    if (!jobId) return;

    setJobs(prev => {
      const existing = prev.get(jobId);
      if (!existing) return prev; // Only track jobs we initiated

      const updated = new Map(prev);
      updated.set(jobId, {
        ...existing,
        status: message.data.status,
        progress: message.data.progress || 0,
        queuePosition: message.data.queue_position,
        estimatedCompletion: message.data.estimated_completion,
        errorMessage: message.data.message,
        resultData: message.data.result ? {
          sessionId: message.data.result.session_id,
          audioUrl: message.data.result.audio_url,
          duration: message.data.result.duration,
        } : existing.resultData,
        completedAt: ['completed', 'failed', 'cancelled'].includes(message.data.status)
          ? new Date().toISOString()
          : undefined,
      });

      return updated;
    });
  }, []);

  // Connect to WebSocket
  useWebSocket({
    enabled: !!user && jobs.size > 0,
    onMessage: handleWebSocketMessage,
  });

  // Poll for updates on active jobs (fallback for WebSocket)
  useEffect(() => {
    const activeJobIds = Array.from(jobs.values())
      .filter(job => ['queued', 'processing'].includes(job.status))
      .map(job => job.id);

    if (activeJobIds.length === 0) {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
      return;
    }

    const pollJobs = async () => {
      for (const jobId of activeJobIds) {
        try {
          const status = await apiClient.get<any>(`/jobs/job/${jobId}`);

          setJobs(prev => {
            const existing = prev.get(jobId);
            if (!existing) return prev;

            const updated = new Map(prev);
            updated.set(jobId, {
              ...existing,
              status: status.status,
              progress: status.progress || 0,
              queuePosition: status.queue_position,
              estimatedCompletion: status.estimated_completion,
              errorMessage: status.error_message,
              resultData: status.result_data ? {
                sessionId: status.result_data.session_id,
                audioUrl: status.result_data.audio_url,
                duration: status.result_data.duration,
              } : existing.resultData,
              completedAt: ['completed', 'failed', 'cancelled'].includes(status.status)
                ? new Date().toISOString()
                : undefined,
            });

            return updated;
          });
        } catch (e) {
          console.error(`Failed to poll job ${jobId}:`, e);
        }
      }
    };

    // Poll every 5 seconds (WebSocket should handle most updates)
    pollingRef.current = setInterval(pollJobs, 5000);
    pollJobs(); // Initial poll

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [jobs]);

  const trackJob = useCallback((jobId: string, type: JobType) => {
    setJobs(prev => {
      const updated = new Map(prev);
      updated.set(jobId, {
        id: jobId,
        type,
        status: 'queued',
        progress: 0,
        createdAt: new Date().toISOString(),
        acknowledged: false,
      });
      return updated;
    });
  }, []);

  const acknowledgeJob = useCallback((jobId: string) => {
    setJobs(prev => {
      const existing = prev.get(jobId);
      if (!existing) return prev;

      const updated = new Map(prev);
      updated.set(jobId, { ...existing, acknowledged: true });
      return updated;
    });
  }, []);

  const clearAcknowledgedJobs = useCallback(() => {
    setJobs(prev => {
      const updated = new Map(prev);
      for (const [id, job] of updated) {
        if (job.acknowledged && ['completed', 'failed', 'cancelled'].includes(job.status)) {
          updated.delete(id);
        }
      }
      return updated;
    });
  }, []);

  // Computed values
  const jobsArray = Array.from(jobs.values());
  const activeJobs = jobsArray.filter(job => ['queued', 'processing'].includes(job.status));
  const recentlyCompleted = jobsArray
    .filter(job => job.status === 'completed')
    .sort((a, b) => new Date(b.completedAt || 0).getTime() - new Date(a.completedAt || 0).getTime())
    .slice(0, MAX_RECENT_JOBS);
  const recentlyFailed = jobsArray
    .filter(job => job.status === 'failed')
    .sort((a, b) => new Date(b.completedAt || 0).getTime() - new Date(a.completedAt || 0).getTime())
    .slice(0, MAX_RECENT_JOBS);

  const value: JobTrackerContextType = {
    activeJobs,
    recentlyCompleted,
    recentlyFailed,
    trackJob,
    acknowledgeJob,
    clearAcknowledgedJobs,
    hasActiveJobs: activeJobs.length > 0,
    hasUnacknowledgedCompletions: recentlyCompleted.some(job => !job.acknowledged),
    hasUnacknowledgedFailures: recentlyFailed.some(job => !job.acknowledged),
  };

  return (
    <JobTrackerContext.Provider value={value}>
      {children}
    </JobTrackerContext.Provider>
  );
};

export const useJobTracker = (): JobTrackerContextType => {
  const context = useContext(JobTrackerContext);
  if (!context) {
    throw new Error('useJobTracker must be used within a JobTrackerProvider');
  }
  return context;
};
